/**
 * The shape a `400` actually arrives in.
 *
 * `CallEmsApi` assigns `await response.json()` — the whole
 * `ValidationProblemDetails` — to a field declared as
 * `iValidationError`, i.e. `{ [key: string]: string[] }`. That declared type
 * is wrong: it describes the `errors` map, not the envelope around it. So
 * TypeScript believes `validationErrors.errors` is a `string[]` when at
 * runtime it is the field map.
 *
 * This local type describes the wire truth. `searchErrorMessages` takes
 * `unknown` and narrows, so the lie is contained to one cast in one file
 * rather than repeated at every call site.
 */
interface ValidationProblemDetails {
    title?: string;
    errors?: Record<string, unknown>;
}

/**
 * Every message in a rejected request, flattened.
 *
 * DELIBERATELY DOES NOT MAP MESSAGES TO FIELDS. The `errors` keys come from
 * three different layers and three different namespaces — a bare property
 * name from MVC (`Direction`), a `Criteria.`-prefixed one from
 * FluentValidation (`Criteria.ItemsPerPage`), and a JSON path from
 * System.Text.Json (`$.statuses[0]`). No client can reliably attach those to
 * controls, so this returns a list and the UI shows it under one heading.
 *
 * The UI's real defence is prevention: `searchCriteria` constrains every
 * control so a `400` is unreachable. One that arrives anyway is a client bug,
 * and is reported as one rather than as user error.
 */
export function searchErrorMessages(body: unknown): string[] {
    if (!body || typeof body !== "object") {
        return [];
    }

    const problem = body as ValidationProblemDetails;
    const messages: string[] = [];

    // `?? {}` on a collection, not a number: a 400 with no `errors` map and one
    // whose map is empty are the same thing to a reader, so there is nothing to
    // distinguish. Non-string entries are dropped rather than coerced — a number
    // where a message belongs is a server contract break, and `String(1)` would
    // put "1" in front of the user as though it were an explanation.
    for (const value of Object.values(problem.errors ?? {})) {
        if (Array.isArray(value)) {
            messages.push(...value.filter((entry): entry is string => typeof entry === "string"));
        }
    }

    if (messages.length === 0 && typeof problem.title === "string") {
        return [problem.title];
    }

    return messages;
}
